import { uuidv7 } from 'uuidv7';

/** Time-ordered ids generated on the client, so records can be created offline. */
export function newId(): string {
  return uuidv7();
}

/** Crockford base32: no I, L, O or U, so codes survive being read aloud or retyped. */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * Random verification code for QR links. 10 characters ≈ 50 bits, never sequential.
 * Printed as "7KQ2M-9XAPT".
 */
export function newVerifyCode(random: (n: number) => Uint8Array = cryptoRandom): string {
  const bytes = random(10);
  let out = '';
  for (const b of bytes) out += ALPHABET[b & 31];
  return out;
}

export function formatVerifyCode(code: string): string {
  return `${code.slice(0, 5)}-${code.slice(5)}`;
}

/** Accept what people type: lower case, dashes, spaces, and the look-alikes I/L/O. */
export function normalizeVerifyCode(input: string): string {
  return input.toUpperCase().replace(/[\s-]/g, '').replace(/[IL]/g, '1').replace(/O/g, '0');
}

function cryptoRandom(n: number): Uint8Array {
  const a = new Uint8Array(n);
  // Web Crypto exists in browsers, Node 24 and Electron; typed locally to stay DOM-free.
  (
    globalThis as unknown as { crypto: { getRandomValues(a: Uint8Array): Uint8Array } }
  ).crypto.getRandomValues(a);
  return a;
}

/**
 * Lab numbers from a pattern such as "QR-{YYYY}-{seq:6}" or "{SITE}-{YYYY}-{seq:6}".
 */
export function formatLabNumber(
  pattern: string,
  values: { seq: number; date: Date; site?: string },
): string {
  return pattern.replace(/\{(\w+)(?::(\d+))?\}/g, (_, key: string, pad?: string) => {
    switch (key) {
      case 'YYYY':
        return String(values.date.getFullYear());
      case 'YY':
        return String(values.date.getFullYear()).slice(-2);
      case 'MM':
        return String(values.date.getMonth() + 1).padStart(2, '0');
      case 'SITE':
        return values.site ?? '';
      case 'seq':
        return String(values.seq).padStart(Number(pad ?? 1), '0');
      default:
        return `{${key}}`;
    }
  });
}

/**
 * A UUID derived from a seed, so two devices that each create "the lab's brand settings"
 * create the same row instead of two.
 */
export async function deterministicId(seed: string): Promise<string> {
  const subtle = (
    globalThis as unknown as {
      crypto: { subtle: { digest(a: string, d: Uint8Array): Promise<ArrayBuffer> } };
    }
  ).crypto.subtle;
  const Encoder = (
    globalThis as unknown as { TextEncoder: new () => { encode(s: string): Uint8Array } }
  ).TextEncoder;
  const hash = new Uint8Array(await subtle.digest('SHA-256', new Encoder().encode(seed)));
  const b = hash.slice(0, 16);
  b[6] = ((b[6] as number) & 0x0f) | 0x80; // version 8: custom
  b[8] = ((b[8] as number) & 0x3f) | 0x80; // RFC 4122 variant
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
